import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import ChangePassword from "./ChangePassword";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuList from "@mui/material/MenuList";
import MenuPopover from "../../components/MenuPopover";
import { s3baseUrl } from "src/config/config";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { getForms_count } from "src/DAL/Forms/forms";
import { useChat } from "src/Hooks/ChatContext";
import { isGuestMember } from "src/utils/guestMember";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrButton from "src/design-system/components/GrButton";
import {
  GrMenuItem,
  grPopoverPaperSx,
} from "src/design-system/components/GrMenu";

const MENU_OPTIONS = [
  {
    label: "Edit Profile",
    icon: icons.person,
    linkTo: "/edit-profile",
  },
];

const paperSx = {
  width: 260,
  maxWidth: `calc(100vw - ${gr.space[8]})`,
  overflow: "hidden",
  ...grPopoverPaperSx,
  "& > .arrow": { display: "none" },
};

const ellipsisSx = {
  m: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export default function AccountPopover() {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { STOP_SOCKET_FORCEFULLY, handleUnreadMessageCount } =
    useContentSetting();
  const { handleCloseChat } = useChat();
  const { pathname } = useLocation();
  const [openPasswordModal, setopenPasswordModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleProfile = (value) => {
    navigate(value);
    handleClose();
  };

  const { userInfo, setUserInfo } = useContentSetting();
  const isGuest = isGuestMember(userInfo);
  const fullName = `${userInfo?.first_name ?? ""} ${
    userInfo?.last_name ?? ""
  }`.trim();
  const handleGetCount = async () => {
    const result = await getForms_count();
    if (result.code === 200) {
      setUserInfo((values) => ({
        ...values,
        formcount: result?.pending_forms,
      }));
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleLogout = async () => {
    handleUnreadMessageCount(0);
    handleCloseChat();
    STOP_SOCKET_FORCEFULLY();
    localStorage.clear();
    navigate("/login");
  };

  const handleChange = () => {
    handleClose();
    navigate(`/dashboard`);
  };

  useEffect(() => {
    handleGetCount();
  }, [pathname]);

  useEffect(() => {
    setTimeout(() => {
      const is_path_includes = (path) => {
        return pathname.toString().includes(path);
      };
      if (!is_path_includes("/forms")) {
        localStorage.removeItem("UserFormSListSearch");
      }
      if (!is_path_includes("/programmes")) {
        localStorage.removeItem("difficultyLevel");
        localStorage.removeItem("isOpen");
      }
      if (
        !is_path_includes("/community-area") &&
        !is_path_includes("/my-posts")
      ) {
        localStorage.removeItem("selectedGroup");
      }
    }, 1000);
  }, [pathname]);

  return (
    <>
      <ChangePassword
        openPasswordModal={openPasswordModal}
        setopenPasswordModal={setopenPasswordModal}
      />
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          padding: 0,
          width: gr.control.touch,
          height: gr.control.touch,
          transition: "box-shadow .15s ease",
          "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
          ...(open && { boxShadow: gr.shadow.focus }),
        }}
      >
        {userInfo !== null &&
        userInfo !== undefined &&
        userInfo !== "undefined" ? (
          <GrAvatar
            src={s3baseUrl + userInfo?.profile_image}
            name={fullName}
            size="md"
          />
        ) : (
          ""
        )}
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={paperSx}
      >
        {userInfo !== null &&
        userInfo !== undefined &&
        userInfo !== "undefined" ? (
          <Box
            onClick={handleChange}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: gr.space[3],
              px: gr.space[4],
              py: gr.space[3],
              cursor: "pointer",
              fontFamily: gr.font.family,
              borderBottom: `1px solid ${gr.color.line}`,
              transition: "background-color .15s ease",
              "&:hover": { background: gr.color.tint },
            }}
          >
            <GrAvatar
              src={s3baseUrl + userInfo?.profile_image}
              name={fullName}
              size="md"
              sx={{ flex: "none" }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="p"
                sx={{
                  ...ellipsisSx,
                  fontSize: gr.font.size.md,
                  fontWeight: gr.font.weight.semibold,
                  color: gr.color.ink,
                }}
              >
                {fullName}
              </Box>
              <Box
                component="p"
                sx={{
                  ...ellipsisSx,
                  fontSize: gr.font.size.sm,
                  color: gr.color.muted,
                }}
              >
                {userInfo?.email}
              </Box>
            </Box>
          </Box>
        ) : (
          ""
        )}

        <MenuList autoFocusItem={open} sx={{ p: gr.space[1] }}>
          {(isGuest ? [] : MENU_OPTIONS).map((option) => (
            <GrMenuItem
              key={option.label}
              onClick={() => handleProfile(option.linkTo)}
            >
              <GrIcon icon={option.icon} size="md" />
              {option.label}
            </GrMenuItem>
          ))}
          {isGuest ? null : (
            <GrMenuItem
              key=""
              onClick={() => {
                handleClose();
                setopenPasswordModal(true);
              }}
            >
              <GrIcon icon={icons.lock} size="md" />
              Change Password
            </GrMenuItem>
          )}
        </MenuList>
        <Box
          sx={{
            p: gr.space[3],
            borderTop: `1px solid ${gr.color.line}`,
          }}
        >
          {/* Neutral outline, as before (the old button was outlined with inherit colour, not teal). */}
          <GrButton
            variant="quiet"
            fullWidth
            startIcon={<GrIcon icon={icons.logOut} size="md" />}
            onClick={() => handleLogout()}
          >
            Logout
          </GrButton>
        </Box>
      </MenuPopover>
    </>
  );
}
